import './Login.css';
import '../common-style.css';

import { useContext, useEffect, useState } from 'react';
import { login, googleLogin, appleLogin, AppleCredential, tryEnableMembership, logOut } from '../../connector/auth';
import { createUser } from '../../connector/api';
import { getAnonImportPath } from '../../connector/anon-migration';
import { FetchError } from 't-lib-ui/connector';
import { AppState } from '../../context/general';
import { createOnChangeHandler, noWhitespaceTrim, validateEmail, validateInput } from 't-lib-ui/helpers';
import { EmailLength } from '../../schema-copy/auth-schema';
import { PasswordLength } from '../../schema-copy/auth-schema';
import { useProgressModal, focusFirstElement } from 't-lib-ui/structure';
import { RouterContext, getLocation, redirect } from 't-lib-ui/router';
import { FlowForm, useFlowFormGlobal, FormGrid, FormGridLine, OrRuler } from 't-lib-ui/structure';
import { Input, PasswordInput, TextButton } from 't-lib-ui/widgets';
import { Link } from 't-lib-ui/router';
import { ErrorNote } from 't-lib-ui/widgets';
import ExternalLoginButtons, { externalLoginFirst, hasExternalLogin } from '../../components/ExternalLoginButtons';
import DownreadLogo from '../../components/DownreadLogo';
import LegalFooter from '../../components/LegalFooter';
import { PendingSignup, setPendingSignup } from './pending-signup';
import { finishConfirmedLogin } from './finish-confirm';

function validatePasswordSimple(password: string): 'bad'|'missing'|undefined {
  if (!password.trim())
    return 'missing';
  return password.length >= 6 && password.length <= 100 ? undefined : 'bad';
}

const emailErrorTexts = {
  bad: 'Please enter a valid email.',
  missing: 'Please enter your email.'
};

const passwordErrorTexts: Record<string, string> = {
  bad: 'Please enter a valid password.',
  missing: 'You must enter a password.',
  wrong: 'Incorrect email or password.'
};

type StatusType = 'OK'|'Connection'|'LoginFailed'|'LoginTimeout'|'PasswordNotSet'|'Blocked'|'Waiting'|'NeedsConfirmation'|'Done';

const statusErrorMessages = {
  Connection: <>Failed to connect to server. Please try again later.</>,
  LoginTimeout: <>Too many bad password attempts. Please wait.</>,
  PasswordNotSet: <>This account has no password set. Please reset your password.</>,
  Blocked: <>This account has been blocked. Please contact support if you think this is a mistake.</>,
};

const Page: React.FC = () => {
  const routerCtx = useContext(RouterContext);
  const location = getLocation(routerCtx);
  const emailFromUrl = noWhitespaceTrim(location.searchParams.get('email') ?? '');
  // Where to go after a successful login. Only same-origin absolute paths are honored (a leading
  // single "/", never "//" which is protocol-relative) so ?return= can't be used as an open redirect.
  // Used by the public share/subscribe pages' "Log in and add" flow to come back and finish adding.
  const returnParam = location.searchParams.get('return');
  const returnTo = returnParam && returnParam.startsWith('/') && !returnParam.startsWith('//') ? returnParam : '/a';
  useFlowFormGlobal();

  const [presentLoading, dismissLoading] = useProgressModal();
  // We no longer store the email in localStorage in plaintext; the browser's own autofill (the email
  // input is autoComplete="username") offers the saved address. Only an explicit ?email= URL prefills.
  const [email, setEmail] = useState(emailFromUrl || '');
  const [emailError, setEmailError] = useState<'bad'|'missing'|undefined>(undefined);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string|undefined>(undefined);
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<StatusType>('OK');
  // Overrides returnTo when the anon session has feeds to offer the freshly logged-in account.
  const [doneTarget, setDoneTarget] = useState<string|undefined>(undefined);
  const appState = useContext(AppState);
  const forgotPasswordPath = `/forgot-password${email ? `?email=${encodeURIComponent(noWhitespaceTrim(email))}` : ''}`;

  useEffect(() => {
    setTimeout(() => {
      // If the browser autofilled the email, focus the password; otherwise focus the email field.
      const emailInput = document.querySelector('input.email') as HTMLInputElement|null;
      if (emailInput?.value) {
        const pw = document.querySelector('input.password') as HTMLElement|null;
        pw ? pw.focus() : focusFirstElement();
      }
      else
        focusFirstElement();
    }, 50);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (status == 'NeedsConfirmation')
    return redirect(routerCtx, "/confirm-email");
  if (status == 'Done')
    return redirect(routerCtx, doneTarget ?? returnTo);

  const validationNeededChecker = () => submitted;

  const loginClicked = (): boolean => {
    setSubmitted(true);
    setStatus('Waiting');
    const trimmedEmail = noWhitespaceTrim(email);

    if (!validateInput(trimmedEmail, validateEmail, setEmailError)) {
      setStatus('OK');
      return true;
    }


    if (!validateInput(password, validatePasswordSimple, setPasswordError)) {
      setStatus('OK');
      return true;
    }

    setBusy(true);

    login(trimmedEmail, password).then(async ({authData, user}) => {
      if (authData.status == 'unconfirmed') {
        appState.setUnconfirmed(authData);
        setStatus('NeedsConfirmation');
      }
      else if (user) {
        appState.setLoggedIn(authData, user);
        setDoneTarget(getAnonImportPath(user, returnTo));
        setStatus('Done');
      }
      else {
        // Confirmed account with no Downread user yet. This happens when a user activates via a
        // password reset instead of the emailed confirmation code (e.g. they lost that email), so
        // the usual post-confirmation user creation never ran. Create it now and finish logging in.
        const newUser = await finishConfirmedLogin(appState, authData);
        setDoneTarget(getAnonImportPath(newUser, returnTo));
        setStatus('Done');
      }
      setBusy(false);
    }).catch((e) => {
      if (e instanceof FetchError && (e.type == 'Blocked' || e.blocked)) {
        setStatus('Blocked');
      }
      else if (e instanceof FetchError && e.type == 'PasswordNotSet') {
        setStatus('PasswordNotSet');
      }
      else if (e instanceof FetchError && e.type == 'LoginFailed') {
        setPasswordError('wrong');
        setStatus('OK');
      }
      else if (e instanceof FetchError && e.type == 'LoginTimeout')
        setStatus('LoginTimeout');
      else
        setStatus('Connection');
      setBusy(false);
    });
    return true;
  };

  // Google and Apple sign-ins. pending: what the ToS step needs if the identity is new.
  const externalLogin = async (provider: string, doLogin: () => ReturnType<typeof googleLogin>, pending: PendingSignup) => {
    presentLoading({
      message: `Signing in with ${provider}...`,
      timeout: 10000,
    });

    try {
      const {authData, user} = await doLogin();

      // If no user exists yet, create one (new signup)
      if (!user) {
        const newUser = await createUser(authData, 'user');
        const {authData: newAuthData, user: enabledUser} = await tryEnableMembership(authData, {memberId: newUser.id, teamId: newUser.teamId});
        dismissLoading();
        appState.setLoggedIn(newAuthData, enabledUser);
        setStatus('Done');
      }
      else {
        // User already exists (returning user)
        dismissLoading();
        appState.setLoggedIn(authData, user);
        setDoneTarget(getAnonImportPath(user, returnTo));
        setStatus('Done');
      }
    } catch (e) {
      dismissLoading();
      if (e instanceof FetchError && e.type == 'TermsNotAccepted') {
        // No account for this identity yet — it's a signup, so the ToS step comes first.
        setPendingSignup(pending);
        return redirect(routerCtx, '/accept-terms');
      }
      setStatus((e instanceof FetchError && ['Connection', 'LoginFailed', 'Blocked'].includes(e.type)) ? e.type as any : 'Connection');
    }
  };

  const handleGoogleSuccess = (credential: string) => externalLogin('Google', () => googleLogin(credential), { googleCredential: credential });
  const handleAppleSuccess = (credential: AppleCredential) => externalLogin('Apple', () => appleLogin(credential), { appleCredential: credential });

  const handleExternalError = (error: string) => {
    console.error('External login error:', error);
    setStatus('Connection');
  };

  const externalLogins = hasExternalLogin && <>
    {!externalLoginFirst && <div className="authDivider"><OrRuler /></div>}
    <ExternalLoginButtons verb="Sign in" onGoogle={handleGoogleSuccess} onApple={handleAppleSuccess} onError={handleExternalError} />
    {externalLoginFirst && <div className="authDivider"><OrRuler /></div>}
  </>;

  // Escape hatch to the anonymous app — also the way out for a user we redirected here because their
  // session cookie was lost (reloginRequired). Drop any stale session so the app boots cleanly in
  // anonymous mode; a first-time visitor is already anonymous, so this is a no-op for them.
  const continueAnonymously = () => {
    if (appState.state.loginState != 'loggedOut') {
      logOut();
      appState.setLoggedOut();
    }
    setStatus('Done');
  };

  return (
    <FlowForm main={true}>
      <div className="authPage">
        <div className="authCard loginCard">
          <div className="authLogo">
            <a href="https://downread.com" className="authLogoLink"><DownreadLogo size={48} showText /><span className="betaBadge" aria-label="Beta">beta</span></a>
          </div>
          <div className="loginIntro">Sign in to your Downread account.</div>

          {appState.state.loginState == 'reloginRequired' && (
            <div className="loginNotice">For your security you've been signed out on this device. Please sign in again to continue.</div>
          )}

          <ErrorNote errorId={status} errorTexts={statusErrorMessages} />

          {externalLoginFirst && externalLogins}

          <FormGrid>
            <FormGridLine span={2} className='authInputRow'>
              <Input
                type="email"
                label="Email"
                layout="singleCol"
                placeholder="email@domain.com"
                autoComplete="username"
                value={email}
                errorId={emailError} errorTexts={emailErrorTexts}
                enterKeyHint="next"
                maxLength={EmailLength}
                className='email'
                onInput={createOnChangeHandler(setEmail, validateEmail, setEmailError, validationNeededChecker)}/>
            </FormGridLine>
            <FormGridLine span={2} className='authInputRow'>
              <PasswordInput
                label="Password"
                layout="singleCol"
                errorId={passwordError} errorTexts={passwordErrorTexts}
                placeholder='Enter password'
                autoComplete='current-password'
                enterKeyHint="enter"
                maxLength={PasswordLength}
                className='password'
                onInput={createOnChangeHandler(setPassword, validatePasswordSimple, setPasswordError, validationNeededChecker)}/>
            </FormGridLine>
            <FormGridLine span={2} align='center' className='authSubmitRow'>
              <TextButton type='major' onClick={loginClicked} className='next' label='Sign In' busy={busy}/>
            </FormGridLine>
          </FormGrid>

          <div className="authLink">
            <Link to={forgotPasswordPath}>Forgot password?</Link>
          </div>

          {!externalLoginFirst && externalLogins}

          <div className="authFooter">
            No account yet? <Link to={'/signup'}>Sign up here</Link>
          </div>
          <div className="authLink">
            <a href="#" onClick={(e) => { e.preventDefault(); continueAnonymously(); }}>Continue without an account</a>
          </div>
        </div>
        {process.env.REACT_APP_BUILD_NUMBER && <div className="buildNumber">b{process.env.REACT_APP_BUILD_NUMBER}</div>}
      </div>
      <LegalFooter />
    </FlowForm>
  );
};

export default Page;
